import { buildApiUrl, API_ENDPOINTS } from '../config/api.js'
import axios from 'axios'

/**
 * Unified data revision service.
 * Wraps POST /api/revise-data to patch rows in day_level_data or curve_features,
 * and/or whitelisted top-level metadata in results.json.
 */

async function revise(sessionId, s3Key, dataset, revisions, metadataPatch = null) {
  const payload = { dataset, revisions: revisions ?? [] }
  if (sessionId) payload.session_id = sessionId
  if (s3Key) payload.s3_key = s3Key
  if (metadataPatch) payload.metadata_patch = metadataPatch

  const response = await axios.post(buildApiUrl(API_ENDPOINTS.REVISE_DATA), payload)
  return response.data
}

export const dataRevisionService = {
  /**
   * Patch one or more day-level rows, optionally with metadata in the same request.
   * @param {string|null} sessionId
   * @param {string|null} s3Key
   * @param {Array<{row_match: object, columns: object}>} revisions
   * @param {object|null} [metadataPatch]
   */
  async reviseDay(sessionId, s3Key, revisions, metadataPatch = null) {
    return revise(sessionId, s3Key, 'day_level_data', revisions, metadataPatch)
  },

  /**
   * Patch one or more curve rows.
   * @param {string|null} sessionId
   * @param {string|null} s3Key
   * @param {Array<{row_match: object, columns: object}>} revisions
   * @param {object|null} [metadataPatch]
   */
  async reviseCurve(sessionId, s3Key, revisions, metadataPatch = null) {
    return revise(sessionId, s3Key, 'curve_features', revisions, metadataPatch)
  },

  /**
   * Patch whitelisted top-level metadata in results.json.
   * @param {string|null} sessionId
   * @param {string|null} s3Key
   * @param {object} metadataPatch
   */
  async reviseMetadata(sessionId, s3Key, metadataPatch) {
    return revise(sessionId, s3Key, 'results_metadata', [], metadataPatch)
  },

  /**
   * Toggle a single day's excluded status.
   * @param {string|null} sessionId
   * @param {string|null} s3Key
   * @param {string} date  - The day's date identifier
   * @param {boolean} excluded
   * @param {string} [note='']
   */
  async excludeDay(sessionId, s3Key, date, excluded, note = '') {
    return revise(sessionId, s3Key, 'day_level_data', [{
      row_match: { date },
      columns: { excluded, exclusion_note: note },
    }])
  },

  /**
   * Override the quality label for a curve (mirrors legacy curveOverrideService).
   * @param {string|null} sessionId
   * @param {string|null} s3Key
   * @param {number} curveId
   * @param {number} overrideFlags  0 = threshold-based, 1 = manual override
   * @param {number|null} overrideDecision  0 = valid, 1 = flagged
   * @param {string} [overrideNote='']
   */
  async overrideCurveQuality(sessionId, s3Key, curveId, overrideFlags, overrideDecision = null, overrideNote = '') {
    const columns = {
      override_flags: overrideFlags,
      override_note: overrideNote,
    }
    if (overrideFlags === 1 && overrideDecision !== null) {
      columns.FLAG_override_decision = overrideDecision
      columns.CURVE_VALID = overrideDecision === 1 ? 0 : 1
    } else if (overrideFlags === 0) {
      columns.FLAG_override_decision = null
    }
    return revise(sessionId, s3Key, 'curve_features', [{
      row_match: { curve_id: curveId },
      columns,
    }])
  },
}

export default dataRevisionService
